/*
===============================================================================================================
                                 Copyright 2023-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

/* eslint-disable no-underscore-dangle */
import React, {
  useContext, useEffect, useState,
  forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { SingleDatePicker } from 'react-dates';
import moment from 'moment';
import ErrorReportingHOC from '@/components/error-reporting-hoc';
import { LocaleContext } from '@/contexts/locale-context';
import TextDiv from '@/components/text-div';
import CalenderIcon from '@/public/icons/calendar.svg';
import 'moment/min/locales.min';
import { formatDateForBackend } from '../../utilities/dates';

/**
 * DatePicker contains an date picker field, header, error message.
 * Is wrapped in the ErrorReporting HOC.
 * @param {string} inputSelectorId - DOM id, used to identify this element
 * @param {string} title - Displayed above the text field
 * @param {string} placeholder - Placeholder text
 * @param {number} inputWidth - CSS Percentage width. Defaults to 100
 * @param {boolean} required - Whether or not this is a required field
 * @param {Object} validationRules - Full description in error-reporting.jsx
 * @param {Object} onChange - On Change function call.
 * @param {string} defaultErrorMessage - Optional. Display this error on component load
 * @param {string} defaultValue - Optional. Input field value on component load
 */
const DatePicker = forwardRef(({
  inputSelectorId,
  title,
  placeholder,
  inputWidth,
  required,
  smallTitle,
  onChange,
  content,
  defaultValue,
  setContent, // Passed in automatically, no need to set
  error, // Passed in automatically, no need to set
}, ref) => {
  const {
    currentLocale, textDirection, dateFormat,
    getIntlLocale,
  } = useContext(LocaleContext);
  const intlLocale = getIntlLocale();
  const [date, setDate] = useState(null);
  const [focusedInput, setFocusedInput] = useState(null);
  const [openCalenderBox, setOpenCalenderBox] = useState(null);
  const [datePickerTextDirection, setDatePickerTextDirection] = useState(null);

  const isOutsideRange = (day) => (moment().startOf('day').diff(day.clone().startOf('day')) < 0);

  let dateString = defaultValue;

  // Swap the month and date to be the wrong way around for the US. This only affects the visuals,
  // the data store still takes the proper date format
  if (!intlLocale && dateString?.length) {
    if (dateFormat.format === 'MM/DD/YYYY') {
      dateString = `${dateString.substr(3, 2)}/${dateString.substr(0, 2)}/${dateString.substr(6, 4)}`;
    } else if (dateFormat.format === 'YYYY/MM/DD') {
      dateString = `${dateString.substr(6, 4)}/${dateString.substr(3, 2)}/${dateString.substr(0, 2)}`;
    }
  }

  const momentObj = intlLocale
    ? moment(dateString, moment.localeData().longDateFormat('L'), true)
    : moment(dateString, dateFormat?.format);

  if (!date && content && momentObj.isValid()) {
    setDate(momentObj);
    const formattedDateForBackend = formatDateForBackend(momentObj);
    onChange(formattedDateForBackend);
  }

  const handleDatesChange = (newDate) => {
    if (newDate) {
      const formattedDate = intlLocale
        ? moment(newDate._d).format(moment.localeData().longDateFormat('L'))
        : moment(newDate._d).locale(dateFormat.locale).format(dateFormat.format);

      setContent(formattedDate);
      const formattedDateForBackend = formatDateForBackend(newDate);

      onChange(formattedDateForBackend);
    }
    setDate(newDate);
  };

  const onFocusChange = ({ focused }) => {
    setFocusedInput(focused);
  };

  const openCalender = () => {
    setOpenCalenderBox(true);
    setFocusedInput(true);
    if (openCalenderBox) {
      return setOpenCalenderBox(false);
    }
    return null;
  };

  useEffect(() => {
    if (textDirection === 'rtl') {
      setDatePickerTextDirection(true);
    } else {
      setDatePickerTextDirection(false);
    }
  }, [textDirection]);

  useEffect(() => {
    if (defaultValue) {
      handleDatesChange(moment(defaultValue));
    }
  }, [defaultValue]);

  useEffect(() => {
    if (!intlLocale) {
      setDate(null);
    }
  }, [currentLocale]);

  const datepickerDateDisplayFormat = intlLocale
    ? moment(date).format(moment.localeData().longDateFormat('L'))
    : moment(date).locale(dateFormat?.locale || 'en').format(dateFormat?.format || 'MM/DD/YYYY');

  return (
    <>
      <div className="date-picker-input">
        {title && (
          <div className={smallTitle ? 'small-title' : 'title'}>
            <TextDiv>
              {`${title}${required === true ? '*' : ''}`}
            </TextDiv>
          </div>
        )}
        <div>
          <span className="date-picker-wrapper">
            <div id="date-picker-input-id" className={`date-picker-input-box${error ? ' errored' : ''}`}>
              <SingleDatePicker
                date={date}
                onDateChange={handleDatesChange}
                focused={focusedInput}
                onFocusChange={onFocusChange}
                numberOfMonths={1}
                placeholder={placeholder}
                id={inputSelectorId}
                required={required}
                block
                hideKeyboardShortcutsPanel
                isRTL={datePickerTextDirection}
                noBorder
                readOnly
                isOutsideRange={isOutsideRange}
                displayFormat={() => datepickerDateDisplayFormat}
              />
              <div className={`date-icon-box${textDirection === 'rtl' ? ' rtl-box' : ''}${error ? ' errored' : ''}`} onClick={() => openCalender()} onKeyDown={() => openCalender()} role="button" tabIndex="0">
                <CalenderIcon />
              </div>
            </div>
          </span>
          <div ref={ref} className={`error ${error ? ' visible' : ''} ${textDirection}`}>
            {error && <TextDiv>{error}</TextDiv>}
          </div>
        </div>
        <style jsx>
          {`

            .date-picker-input {
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              width: ${inputWidth}%;
            }

            @media only screen and (max-width: 768px) {
              .date-picker-input {
                width: 100%;
              }
              .error.visible {
                font-size: var(--font-size-error);
              }
            }

            @media only screen and (min-width: 768px) {
              .error.visible {
                font-size: var(--font-size-error);
              }
            }
            
            .date-picker-wrapper {
              position: relative;
            }

            .date-picker-input-box {
              background: var(--color-white);
              border-radius: 3px;
              border: 1px solid var(--color-mid-grey);
              color: var(--color-primary-dark);
              display: flex;
              font-size: var(--font-size-body);
              font-weight: var(--font-weight-body);
              height: 48px;
              position: relative;
              width: 100%;
            }

            .date-picker-input-box.errored {
              border: 1px solid var(--color-error-red);
              font-size: var(--font-size-error);
            }

            .date-icon-box {
              border-radius: 3px;
              border: 1px solid var(--color-mid-grey);
              cursor: pointer;
              height: 48px;
              padding: 10px;
              position: absolute;
              right: -1px;
              top: -1px;
              width: 48px;
            }

            .date-icon-box.errored {
              border: 1px solid var(--color-error-red);
            }

            .date-icon-box.rtl-box {
              left: -1px;
              top: -1px;
            }

            .date-icon-box.rtl-box.errored {
              border: 1px solid var(--color-error-red);
            }

            .error {
              min-height: 20px;
              margin-top: 3px;
              visibility: hidden;
              white-space: break-spaces;
            }

            .error.visible {
              color: var(--color-error-red);
              font-size: var(--font-size-error);
              text-align: right;
              visibility: visible;
            }

            .error.rtl {
              text-align: left;
            }

            .title {
              color: var(--color-almost-black);
              display: block;
              font-size: var(--font-size-H2);
              font-weight: var(--font-weight-H2);
              line-height: 2;
            }

            .small-title {
              color: var( --color-title-grey);
              font-size: var(--font-size-address);
              margin-bottom: 12px;
            }
        `}
        </style>
      </div>
    </>
  );
});

DatePicker.defaultProps = {
  title: null,
  placeholder: '',
  inputWidth: 100,
  required: false,
  smallTitle: false,
  validationRules: {
    onSubmit: null,
    required: '',
    submitErrors: null,
    tooLongError: '',
  },
  error: null,
  onChange: null,
  content: '',
  defaultValue: '',
};

DatePicker.propTypes = {
  inputSelectorId: PropTypes.string.isRequired,
  title: PropTypes.string,
  placeholder: PropTypes.string,
  inputWidth: PropTypes.number,
  required: PropTypes.bool,
  smallTitle: PropTypes.bool,
  validationRules: PropTypes.shape({
    onSubmit: PropTypes.arrayOf(PropTypes.string),
    requiredError: PropTypes.string,
    submitErrors: PropTypes.arrayOf(PropTypes.string),
    tooLongError: PropTypes.string,
  }),
  setContent: PropTypes.func.isRequired,
  content: PropTypes.string,
  error: PropTypes.string,
  onChange: PropTypes.func,
  defaultValue: PropTypes.string,
};

export default ErrorReportingHOC(DatePicker);
